import { useApiQuery } from '../../api';
import { asyncOperationQueries } from './asyncOperations.queries';
import type {
  AsyncOperation,
  OperationReference,
} from './asyncOperations.types';
import {
  createAsyncOperationRequestError,
  getAsyncOperationClientState,
  isAsyncOperationNotFoundError,
  isTransientAsyncOperationError,
} from './asyncOperations.utils';

export function useAsyncOperationQuery(
  reference: OperationReference,
  options: { enabled?: boolean } = {}
) {
  const enabled = options.enabled ?? true;
  const descriptor = asyncOperationQueries.detail(reference);
  const query = useApiQuery<AsyncOperation, OperationReference>(
    async (api, signal, variables) => {
      const result = await api.GET('/operations/{id}', {
        headers: {
          ...(variables.placement.dataRegion
            ? { 'x-data-region': variables.placement.dataRegion }
            : {}),
          'x-team-id': variables.requesterTeamId,
        },
        params: { path: { id: variables.operationId } },
        signal,
      });

      if (result.error) {
        throw createAsyncOperationRequestError(
          result.response.status,
          result.error
        );
      }

      return result.data;
    },
    {
      ...descriptor,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      retry: false,
      skip: !enabled,
    }
  );

  return {
    ...query,
    isNotFound: isAsyncOperationNotFoundError(query.error),
    isTransientError: isTransientAsyncOperationError(query.error),
    operationState: getAsyncOperationClientState({
      data: query.data,
      enabled,
      error: query.error,
      isPending: query.isPending,
    }),
  };
}
